import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ZegoUIKitPrebuilt } from "@zegocloud/zego-uikit-prebuilt";
import {
  FaArrowLeft,
  FaLock,
  FaVideo,
  FaPhoneSlash,
  FaClock,
  FaClipboard,
  FaCalendarAlt,
  FaStethoscope,
  FaWifi,
  FaMicrophone,
  FaUserMd,
  FaRedo,
} from "react-icons/fa";
import Header from "../Components/Header";
import LoadingState from "../Components/States/LoadingState";
import { getAssetUrl } from "../api/config";
import { getStaffId } from "../utils/storage";
import { buildKitToken, getRoomId, ZEGO_ROLE_PREFIX } from "../utils/Zegocloud";
import { joinRoom, endRoom, getRoomDetails, hasOpenSession } from "../api/ZegoApi";

/* ---------- helpers ---------- */

const getPatient = () => {
  const staffId = getStaffId();
  let name = "Patient";
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const d = u?.data || u;
    name = String(d.name || d.fullName || "Patient");
  } catch (_) {}
  return { id: staffId || `guest_${Date.now()}`, name };
};

const formatTime = (total) => {
  const h = Math.floor(total / 3600);
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const resolveImage = (src) => {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("blob:") || src.startsWith("data:")) return src;
  return getAssetUrl(src);
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-surface-container/60 last:border-0">
    <span className="flex items-center gap-2 text-sm font-semibold text-on-primary-fixed-var shrink-0">
      <Icon className="text-primary shrink-0" size={14} />
      {label}
    </span>
    <span className="text-sm text-on-surface-variant text-right break-words min-w-0">
      {value || "—"}
    </span>
  </div>
);

const TIPS = [
  { icon: FaWifi, text: "Use a stable internet connection (Wi-Fi recommended)." },
  { icon: FaVideo, text: "Allow camera access when your browser asks." },
  { icon: FaMicrophone, text: "Allow microphone access and keep your surroundings quiet." },
];

/* ---------- component ---------- */

const PatientVideoCall = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const containerRef = useRef(null);
  const zpRef = useRef(null);
  const runIdRef = useRef(0);
  const activeRef = useRef(true);
  const navigateRef = useRef(navigate);
  const joinedRef = useRef(false); // Zego room actually joined in this run
  const endedRef = useRef(false); // endroom already sent for this run

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(false);
  const [doctorPresent, setDoctorPresent] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [details, setDetails] = useState(null); // /zego/roomdetails response

  // API data wins; router state is the fallback (e.g. instant render before the fetch returns)
  const doctor = details?.doctor || state?.doctor;
  const booking = details?.booking;

  // keep latest navigate without re-running the join effect
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  /* ---- room details ---- */
  const loadDetails = useCallback(async () => {
    if (!bookingId) return null;
    try {
      const res = await getRoomDetails(bookingId);
      setDetails(res);
      return res;
    } catch (e) {
      console.error("Room details error:", e);
      return null;
    }
  }, [bookingId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  /* ---- end-room (runs once per join) ---- */
  const finishCall = useCallback(async () => {
    if (endedRef.current || !joinedRef.current) return;
    endedRef.current = true;
    try {
      await endRoom(bookingId);
    } catch (e) {
      console.error("End room error:", e);
    }
  }, [bookingId]);

  // tab close / refresh / hard navigation: fire-and-forget with keepalive
  useEffect(() => {
    const onPageHide = () => {
      if (!joinedRef.current || endedRef.current) return;
      endedRef.current = true;
      endRoom(bookingId, { keepalive: true }).catch(() => {});
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [bookingId]);

  // call timer
  useEffect(() => {
    if (!joined) return undefined;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [joined]);

  const destroyCall = useCallback(() => {
    try {
      zpRef.current?.destroy();
    } catch (_) {}
    zpRef.current = null;
  }, []);

  const leaveCall = useCallback(async () => {
    activeRef.current = false;
    destroyCall();
    setJoined(false);
    await finishCall();
    navigateRef.current("/home", { replace: true });
  }, [destroyCall, finishCall]);

  useEffect(() => {
    activeRef.current = true;
    joinedRef.current = false;
    endedRef.current = false;
    const runId = ++runIdRef.current;

    const start = async () => {
      setLoading(true);
      setError("");
      setJoined(false);
      setDoctorPresent(false);
      setSeconds(0);

      try {
        if (!bookingId) throw new Error("Booking ID is missing.");

        const patient = getPatient();
        const roomId = getRoomId(bookingId);
        const userId = `${ZEGO_ROLE_PREFIX.patient}${patient.id}`;
        console.log("ZEGO ROOM:", roomId, "| USER:", userId);

        const kitToken = await buildKitToken({
          roomId,
          userId,
          userName: patient.name,
        });

        // a newer run started or the page unmounted while awaiting
        if (runId !== runIdRef.current || !activeRef.current || !containerRef.current) return;

        const zp = ZegoUIKitPrebuilt.create(kitToken);
        zpRef.current = zp;

        zp.joinRoom({
          container: containerRef.current,
          scenario: { mode: ZegoUIKitPrebuilt.OneONoneCall },
          maxUsers: 2,
          showPreJoinView: true,
          turnOnCameraWhenJoining: true,
          turnOnMicrophoneWhenJoining: true,
          showScreenSharingButton: false,
          showRoomDetailsButton:false,
          showTextChat: false,
          showUserList: false,
          showLeavingView: false,
          onJoinRoom: () => {
            if (runId !== runIdRef.current) return;
            joinedRef.current = true;
            setJoined(true);

            // record the join on the backend, then refresh details
            joinRoom(bookingId)
              .catch((e) => console.error("Join record error:", e))
              .then(() => loadDetails())
              .then((res) => {
                if (res && runId === runIdRef.current && hasOpenSession(res, "doctor")) {
                  setDoctorPresent(true);
                }
              });
          },
          onUserJoin: () => {
            if (runId === runIdRef.current) setDoctorPresent(true);
          },
          onUserLeave: () => {
            if (runId === runIdRef.current) setDoctorPresent(false);
          },
          onLeaveRoom: () => {
            if (runId !== runIdRef.current || !activeRef.current) return;
            activeRef.current = false;
            setJoined(false);
            finishCall().finally(() => {
              navigateRef.current("/consultations", { replace: true });
            });
          },
        });

        setLoading(false);
      } catch (e) {
        console.error("Zego join error:", e);
        if (runId === runIdRef.current && activeRef.current) {
          setError(e?.message || "Unable to start video consultation.");
          setLoading(false);
        }
      }
    };

    start();

    return () => {
      activeRef.current = false;
      finishCall(); // closes the session if the user navigates away mid-call
      runIdRef.current += 1;
      destroyCall();
    };
  }, [bookingId, attempt, destroyCall, finishCall, loadDetails]);

  const doctorName = doctor?.name
    ? `Dr. ${String(doctor.name).replace(/^Dr\.?\s*/i, "")}`
    : "Doctor Consultation";
  const doctorImg = !imgFailed ? resolveImage(doctor?.image || doctor?.profileImage) : "";
  const dateText = formatDate(booking?.bookedSlot?.date || booking?.date);
  const slotText = booking?.bookedSlot?.timeSlot || booking?.timeSlot;

  const statusLabel = !joined
    ? "Ready to join"
    : doctorPresent
      ? "Doctor connected"
      : "Waiting for doctor";
  const statusClass = joined && doctorPresent
    ? "bg-primary text-white"
    : "bg-primary-fixed/40 text-on-primary-fixed-var";

  return (
    <div className="min-h-screen bg-surface-low flex flex-col">
      <Header />

      <div className="flex-1 pb-16 lg:pb-0">
        <div className="max-w-2xl lg:max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8 pt-20">
          {/* Title row */}
          <div className="mb-5 sm:mb-6 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 min-h-[36px] text-sm font-medium text-on-surface-variant hover:text-on-primary-fixed-var transition-colors"
              >
                <FaArrowLeft size={12} /> Back
              </button>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-on-primary-fixed-var mt-1">
                Video Consultation
              </h1>
              <p className="text-on-surface-variant mt-1 text-xs sm:text-sm">
                Your private, secure consultation room.
              </p>
            </div>

            <span className="hidden sm:inline-flex shrink-0 items-center gap-2 bg-surface-lowest border border-surface-container rounded-full px-3 py-1.5 text-xs font-semibold text-on-primary-fixed-var">
              <FaLock className="text-primary" size={11} />
              End-to-end secure
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] lg:grid-rows-[auto_1fr] gap-4 sm:gap-6 items-start">
            {/* Doctor + booking card */}
            <section className="lg:col-start-1 lg:row-start-1 bg-surface-lowest border border-surface-container rounded-xl p-4 sm:p-5">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full border-4 border-white shadow-md bg-primary-fixed/40 text-primary flex items-center justify-center overflow-hidden shrink-0">
                  {doctorImg ? (
                    <img
                      src={doctorImg}
                      alt={doctorName}
                      className="h-full w-full object-cover"
                      onError={() => setImgFailed(true)}
                    />
                  ) : (
                    <FaUserMd size={22} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-bold text-on-primary-fixed-var text-base sm:text-lg leading-snug break-words">
                    {doctorName}
                  </h2>
                  <p className="text-sm text-on-surface-variant break-words">
                    {doctor?.specialization || "Video Consultation"}
                  </p>
                </div>
              </div>

              <div className="mt-3 border-t border-surface-container/60 pt-1">
                <InfoRow icon={FaClipboard} label="Booking ID" value={bookingId} />
                {doctor?.qualification && (
                  <InfoRow icon={FaStethoscope} label="Qualification" value={doctor.qualification} />
                )}
                {dateText && <InfoRow icon={FaCalendarAlt} label="Date" value={dateText} />}
                {slotText && <InfoRow icon={FaClock} label="Time Slot" value={slotText} />}
                <InfoRow icon={FaVideo} label="Mode" value="Online video call" />
              </div>

              {joined && (
                <button
                  type="button"
                  onClick={leaveCall}
                  className="mt-4 w-full min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl border border-red-300 text-error text-sm font-semibold hover:bg-red-50 transition-colors"
                >
                  <FaPhoneSlash size={14} />
                  Leave consultation
                </button>
              )}
            </section>

            {/* Call area */}
            <section className="lg:col-start-2 lg:row-start-1 lg:row-span-2 bg-surface-lowest border border-surface-container rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-surface-container/60">
                <div className="flex items-center gap-2 min-w-0">
                  <FaVideo className="text-primary shrink-0" />
                  <h2 className="font-semibold text-on-primary-fixed-var truncate">
                    Consultation Room
                  </h2>
                </div>
                <span className="sm:hidden inline-flex items-center gap-1.5 text-xs font-medium text-primary shrink-0">
                  <FaLock size={10} /> Secure
                </span>
              </div>

              {error ? (
                <div className="px-4 py-12 sm:py-16 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-error-container text-error flex items-center justify-center mb-4">
                    <FaVideo size={22} />
                  </div>
                  <p className="font-semibold text-on-primary-fixed-var">
                    Couldn't start the call
                  </p>
                  <p className="text-sm text-on-surface-variant mt-1 mb-5 max-w-sm mx-auto break-words">
                    {error}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <button
                      type="button"
                      onClick={() => setAttempt((a) => a + 1)}
                      className="min-h-[44px] inline-flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-container transition-colors"
                    >
                      <FaRedo size={12} /> Try again
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/consultations", { replace: true })}
                      className="min-h-[44px] px-5 py-2 rounded-xl border border-surface-container text-sm font-medium text-on-primary-fixed-var hover:bg-surface-low transition-colors"
                    >
                      Back to bookings
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  {loading && (
                    <div className="absolute inset-0 z-10 bg-surface-lowest">
                      <LoadingState branded message="Setting up your consultation…" className="py-16" />
                    </div>
                  )}
                  {/* Zego needs an explicit height, otherwise it renders blank */}
                  <div
                    ref={containerRef}
                    className="w-full bg-surface-low"
                    style={{
                      height: "min(72vh, 720px)",
                      minHeight: 420,
                    }}
                  />
                </div>
              )}
            </section>

            {/* Tips */}
            <section className="lg:col-start-1 lg:row-start-2 bg-surface-lowest border border-surface-container rounded-xl p-4 sm:p-5">
              <h2 className="font-semibold text-on-primary-fixed-var mb-3">
                Before you join
              </h2>
              <ul className="space-y-3">
                {TIPS.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-primary-fixed/40 text-primary flex items-center justify-center">
                      <Icon size={12} />
                    </span>
                    <span className="text-sm text-on-surface-variant leading-relaxed">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-on-surface-variant mt-4 leading-relaxed">
                Tap <span className="font-semibold text-on-primary-fixed-var">Join</span> on
                the preview screen when you're ready. Your doctor will connect shortly.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientVideoCall;